/** @jsxRuntime automatic */
/** @jsxImportSource react */
import React, { useEffect, useId, useRef, useState } from "react"
import { createPortal } from "react-dom"
import { TbAlignLeft, TbAlignCenter, TbAlignRight, TbPlus, TbTable, TbTrash, TbX } from "react-icons/tb"
import { removeFreeStyleAxis } from "./dashboard-widget-data"
import { EditorNumberInput } from "./WidgetEditorFields"

export function FreeStyleEditor(props) {
  const [open, setOpen] = useState(false)
  return (
    <>
      <div className="datox-react-dashboard__content-card">
        <TbTable size={22} aria-hidden="true" />
        <div>
          <strong>{props.labels.tableContent}</strong>
          <p>
            {props.labels.tableDimensions
              .replace("{rows}", String(props.value.rows.length))
              .replace("{columns}", String(props.value.headers.length))}
          </p>
        </div>
        <button
          type="button"
          className="datox-react-dashboard__button datox-react-dashboard__button--primary"
          onClick={() => setOpen(true)}
        >
          {props.labels.editTableContent}
        </button>
      </div>
      <p className="datox-react-dashboard__settings-help">{props.labels.independentTableHelp}</p>
      {open ? <FreeStyleGrid {...props} onClose={() => setOpen(false)} /> : null}
    </>
  )
}

function FreeStyleGrid(props) {
  const id = useId()
  const dialog = useRef(null)
  const [selected, setSelected] = useState({ row: 0, col: 0 })
  const value = props.value
  const row = Math.min(selected.row, value.rows.length - 1)
  const col = Math.min(selected.col, value.headers.length - 1)
  const change = (patch) => props.onChange({ ...value, ...patch })
  const select = (row, col) =>
    setSelected((previous) => (previous.row === row && previous.col === col ? previous : { row, col }))
  useEffect(() => {
    const previous = document.activeElement
    dialog.current?.querySelector("textarea")?.focus()
    return () => {
      if (previous instanceof HTMLElement && previous.isConnected) previous.focus()
    }
  }, [])
  const onKeyDown = (event) => {
    if (event.key === "Escape") {
      event.preventDefault()
      event.stopPropagation()
      props.onClose()
      return
    }
    if (event.key !== "Tab") return
    const items = [
      ...dialog.current.querySelectorAll(
        'button:not(:disabled), input:not(:disabled), select:not(:disabled), textarea:not(:disabled), [tabindex="0"]',
      ),
    ]
    const first = items[0]
    const last = items.at(-1)
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault()
      last?.focus()
    }
    if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault()
      first?.focus()
    }
  }
  // Match the source panel: the editor lives outside the canvas zoom/transform.
  return createPortal(
    <div
      className="datox-react-dashboard__dialog-backdrop datox-react-dashboard__free-grid-backdrop"
      onClick={(event) => {
        if (event.target === event.currentTarget) props.onClose()
      }}
    >
      <section
        className="datox-react-dashboard__free-grid-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby={`${id}-title`}
        aria-describedby={`${id}-help`}
        ref={dialog}
        onKeyDown={onKeyDown}
      >
        <header className="datox-react-dashboard__free-grid-header">
          <div>
            <h2 id={`${id}-title`}>{props.labels.editTableContent}</h2>
            <p>
              {props.labels.tableDimensions
                .replace("{rows}", String(value.rows.length))
                .replace("{columns}", String(value.headers.length))}
            </p>
          </div>
          <button
            type="button"
            className="datox-react-dashboard__button"
            aria-label={props.labels.closePanel}
            onClick={props.onClose}
          >
            <TbX size={18} aria-hidden="true" />
          </button>
        </header>
        <div className="datox-react-dashboard__free-grid-toolbar">
          <button
            type="button"
            className="datox-react-dashboard__button"
            disabled={value.rows.length >= 50}
            onClick={() =>
              change({ rows: [...value.rows, value.headers.map(() => "")], rowTypes: [...value.rowTypes, "default"] })
            }
          >
            <TbPlus size={16} aria-hidden="true" />
            {props.labels.addRow}
          </button>
          <button
            type="button"
            className="datox-react-dashboard__button"
            disabled={value.headers.length >= 20}
            onClick={() =>
              change({
                headers: [
                  ...value.headers,
                  props.labels.columnName.replace("{count}", String(value.headers.length + 1)),
                ],
                columnWidths: [...value.columnWidths, 168],
                columnTypes: [...value.columnTypes, "text"],
                rows: value.rows.map((cells) => [...cells, ""]),
              })
            }
          >
            <TbPlus size={16} aria-hidden="true" />
            {props.labels.addColumn}
          </button>
          <p id={`${id}-help`}>{props.labels.gridEditingHelp}</p>
        </div>
        {value.rows.length >= 50 || value.headers.length >= 20 ? (
          <p className="datox-react-dashboard__settings-help" role="status">
            {props.labels.freeStyleLimit}
          </p>
        ) : null}
        <div className="datox-react-dashboard__free-grid-body">
          <div className="datox-react-dashboard__free-grid-viewport">
            <table className="datox-react-dashboard__free-grid" aria-label={props.labels.tableContent}>
              <thead>
                <tr>
                  <th scope="col" aria-label={props.labels.rowNumber}>
                    <TbTable size={16} aria-hidden="true" />
                    <span className="datox-react-dashboard__sr-only">{props.labels.rowNumber}</span>
                  </th>
                  {value.headers.map((header, column) => (
                    <th
                      scope="col"
                      key={column}
                      style={{
                        width: value.columnWidths?.[column] || 168,
                        minWidth: value.columnWidths?.[column] || 168,
                      }}
                    >
                      <input
                        aria-label={props.labels.columnName.replace("{count}", String(column + 1))}
                        maxLength={300}
                        value={header}
                        onFocus={() => select(row, column)}
                        onChange={(event) =>
                          change({
                            headers: value.headers.map((name, index) =>
                              index === column ? event.currentTarget.value : name,
                            ),
                          })
                        }
                      />
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {value.rows.map((cells, rowIndex) => (
                  <tr
                    key={rowIndex}
                    className={value.rowTypes?.[rowIndex] === "section" ? "datox-react-dashboard__section-row" : ""}
                  >
                    <th scope="row">{rowIndex + 1}</th>
                    {cells.map((cell, column) => (
                      <td key={column} data-selected={rowIndex === row && column === col ? "true" : undefined}>
                        <textarea
                          rows={1}
                          maxLength={500}
                          aria-label={props.labels.cellPosition
                            .replace("{row}", String(rowIndex + 1))
                            .replace("{column}", String(column + 1))}
                          value={cell ?? ""}
                          style={{ textAlign: value.cellAlign?.[`${rowIndex}:${column}`] || "left" }}
                          onFocus={() => select(rowIndex, column)}
                          onChange={(event) =>
                            change({
                              rows: value.rows.map((cells, index) =>
                                index === rowIndex
                                  ? cells.map((text, colIndex) =>
                                      colIndex === column ? event.currentTarget.value : text,
                                    )
                                  : cells,
                              ),
                            })
                          }
                        />
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <aside className="datox-react-dashboard__free-grid-inspector" aria-label={props.labels.selectedCellSettings}>
            <strong>
              {props.labels.cellPosition.replace("{row}", String(row + 1)).replace("{column}", String(col + 1))}
            </strong>
            <div className="datox-react-dashboard__field-label">{props.labels.cellAlignment}</div>
            <div className="datox-react-dashboard__mini-segmented" role="group" aria-label={props.labels.cellAlignment}>
              {[
                ["left", props.labels.alignLeft, TbAlignLeft],
                ["center", props.labels.alignCenter, TbAlignCenter],
                ["right", props.labels.alignRight, TbAlignRight],
              ].map(([alignment, label, Icon]) => (
                <button
                  key={alignment}
                  type="button"
                  className={`datox-react-dashboard__mini-segment ${(value.cellAlign?.[`${row}:${col}`] || "left") === alignment ? "datox-react-dashboard__mini-segment--active" : ""}`}
                  aria-label={label}
                  title={label}
                  aria-pressed={(value.cellAlign?.[`${row}:${col}`] || "left") === alignment}
                  onClick={() => change({ cellAlign: { ...value.cellAlign, [`${row}:${col}`]: alignment } })}
                >
                  <Icon size={18} aria-hidden="true" />
                </button>
              ))}
            </div>
            <label>
              <span>{props.labels.columnWidth}</span>
              <EditorNumberInput
                min={72}
                max={2000}
                value={value.columnWidths?.[col] || 168}
                onChange={(width) =>
                  change({
                    columnWidths: value.columnWidths.map((current, index) => (index === col ? width : current)),
                  })
                }
              />
            </label>
            <label>
              <span>{props.labels.valueFormat}</span>
              <select
                className="datox-react-dashboard__select"
                value={value.columnTypes?.[col] || "text"}
                onChange={(event) =>
                  change({
                    columnTypes: value.columnTypes.map((current, index) =>
                      index === col ? event.currentTarget.value : current,
                    ),
                  })
                }
              >
                <option value="text">{props.labels.textFormat}</option>
                <option value="number">{props.labels.numberFormat}</option>
                <option value="percent">{props.labels.percentFormat}</option>
              </select>
            </label>
            {value.columnTypes?.[col] === "percent" ? (
              <p className="datox-react-dashboard__settings-help">{props.labels.percentFormatHelp}</p>
            ) : null}
            <label>
              <span>{props.labels.rowName.replace("{count}", String(row + 1))}</span>
              <select
                className="datox-react-dashboard__select"
                value={value.rowTypes?.[row] || "default"}
                onChange={(event) =>
                  change({
                    rowTypes: value.rowTypes.map((current, index) =>
                      index === row ? event.currentTarget.value : current,
                    ),
                  })
                }
              >
                <option value="default">{props.labels.normalRow}</option>
                <option value="section">{props.labels.sectionRow}</option>
              </select>
            </label>
            <div className="datox-react-dashboard__free-grid-remove">
              <button
                type="button"
                className="datox-react-dashboard__button datox-react-dashboard__button--danger"
                disabled={value.rows.length <= 1}
                onClick={() => {
                  props.onChange(removeFreeStyleAxis(value, "row", row))
                  select(Math.max(0, row - 1), col)
                }}
              >
                <TbTrash size={14} aria-hidden="true" />
                {props.labels.removeRow}
              </button>
              <button
                type="button"
                className="datox-react-dashboard__button datox-react-dashboard__button--danger"
                disabled={value.headers.length <= 1}
                onClick={() => {
                  props.onChange(removeFreeStyleAxis(value, "column", col))
                  select(row, Math.max(0, col - 1))
                }}
              >
                <TbTrash size={14} aria-hidden="true" />
                {props.labels.removeColumn}
              </button>
            </div>
          </aside>
        </div>
        <footer className="datox-react-dashboard__free-grid-footer">
          <span>{props.labels.gridSaveHelp}</span>
          <button
            type="button"
            className="datox-react-dashboard__button datox-react-dashboard__button--primary"
            onClick={props.onClose}
          >
            {props.labels.doneEditing}
          </button>
        </footer>
      </section>
    </div>,
    document.body,
  )
}
